/**
 * Message direction + origin classification (Order 31A).
 * Shared by Reports query layer, write-path helpers, and future export (31G).
 */
import { CAMPAIGN_MESSAGE_PROVENANCE_KIND, CAMPAIGN_TEST_MESSAGE_PROVENANCE_KIND, } from "./campaign-contracts.js";
import { MESSAGE_ORIGINS } from "./constants.js";
const ORIGIN_SET = new Set(MESSAGE_ORIGINS);
export function isMessageOriginName(value) {
    return typeof value === "string" && ORIGIN_SET.has(value);
}
/**
 * Direction derivation from durable fields + *effective* origin
 * (callers should pass resolveReportMessageOrigin first for historical rows).
 *
 * - OUTBOUND when a human member sent it OR effective origin is set
 *   (future AI/Automation/API may omit organizationMemberId; historical Campaign
 *   rows may also lack both durable origin and member until classified via
 *   CampaignRecipient / campaignContext)
 * - INBOUND when both member and effective origin are null
 */
export function deriveMessageDirection(input) {
    if (input.origin != null) {
        return "OUTBOUND";
    }
    if (input.organizationMemberId != null) {
        return "OUTBOUND";
    }
    return "INBOUND";
}
/**
 * Map campaignContext.kind → MessageOrigin for new writes.
 * Callers without campaignContext should pass INBOX explicitly for inbox sends.
 */
export function messageOriginFromCampaignContextKind(kind) {
    if (kind === CAMPAIGN_MESSAGE_PROVENANCE_KIND) {
        return "CAMPAIGN";
    }
    if (kind === CAMPAIGN_TEST_MESSAGE_PROVENANCE_KIND) {
        return "CAMPAIGN_SEND_TEST";
    }
    return null;
}
/**
 * Historical / read-time origin resolution.
 * Prefer durable Message.origin. Fall back only with strong signals.
 * Do NOT invent INBOX for unclassified historical outbound.
 */
export function resolveReportMessageOrigin(input) {
    if (input.storedOrigin != null && isMessageOriginName(input.storedOrigin)) {
        return input.storedOrigin;
    }
    if (input.hasCampaignRecipient) {
        return "CAMPAIGN";
    }
    const fromContext = messageOriginFromCampaignContextKind(input.campaignContextKind);
    if (fromContext != null) {
        return fromContext;
    }
    return null;
}
/**
 * Single classification path for Reports list DTO + filter semantics.
 * Filters must match these effective values (not durable columns alone).
 */
export function classifyReportMessage(input) {
    const origin = resolveReportMessageOrigin({
        storedOrigin: input.storedOrigin,
        hasCampaignRecipient: input.hasCampaignRecipient,
        campaignContextKind: input.campaignContextKind,
    });
    return {
        origin,
        direction: deriveMessageDirection({
            organizationMemberId: input.organizationMemberId,
            origin,
        }),
    };
}
/**
 * True when historical/durable evidence marks an outbound Campaign execution row.
 * Used by SQL filters so direction/origin queries agree with classifyReportMessage.
 */
export function hasHistoricalCampaignOutboundEvidence(input) {
    return (input.hasCampaignRecipient ||
        input.campaignContextKind === CAMPAIGN_MESSAGE_PROVENANCE_KIND);
}
export function hasHistoricalCampaignSendTestEvidence(input) {
    return input.campaignContextKind === CAMPAIGN_TEST_MESSAGE_PROVENANCE_KIND;
}
export function truncateReportPreview(content, maxChars) {
    if (content == null) {
        return null;
    }
    const trimmed = content.trim();
    if (trimmed.length === 0) {
        return null;
    }
    if (trimmed.length <= maxChars) {
        return trimmed;
    }
    return `${trimmed.slice(0, Math.max(0, maxChars - 1))}…`;
}
export function campaignContextKindFromStructuredData(structuredData) {
    if (!structuredData || typeof structuredData !== "object" || Array.isArray(structuredData)) {
        return null;
    }
    const context = structuredData["campaignContext"];
    if (!context || typeof context !== "object" || Array.isArray(context)) {
        return null;
    }
    const kind = context["kind"];
    return typeof kind === "string" ? kind : null;
}
export function templateMetaFromStructuredData(structuredData) {
    if (!structuredData || typeof structuredData !== "object" || Array.isArray(structuredData)) {
        return null;
    }
    const root = structuredData;
    if (root["kind"] !== "whatsapp_template_send") {
        return null;
    }
    const template = root["template"];
    if (!template || typeof template !== "object" || Array.isArray(template)) {
        return null;
    }
    const t = template;
    const name = typeof t["name"] === "string" ? t["name"] : null;
    if (!name) {
        return null;
    }
    const rawId = t["whatsappTemplateId"];
    const whatsappTemplateId = typeof rawId === "number" && Number.isInteger(rawId) && rawId > 0
        ? rawId
        : typeof rawId === "string" && /^\d+$/.test(rawId)
            ? Number(rawId)
            : null;
    const languageCode = typeof t["languageCode"] === "string" ? t["languageCode"] : null;
    return { whatsappTemplateId, name, languageCode };
}
//# sourceMappingURL=message-origin.js.map